import { formatCurrency } from 'common/FormatUtilities';
import React, { useEffect, useRef, useState } from 'react';
import { to, useSpring } from 'react-spring';
import { Confetti } from 'src/shared/design-system//Confetti';
import { TextReplace } from 'src/shared/design-system/Utilities/TextReplace';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { Transition } from 'web-common/src/shared/styles/transition';
import {
  ADD_AMOUNT_MESSAGE,
  DEFAULT_FREE_DELIVERY_THRESHOLD,
  DISCOUNTED_DELIVERY_MESSAGE,
  DISCOUNTED_DELIVERY_TEXT,
  SHARED_CONFETTI_PROPS,
} from '../consts';
import { FreeDeliveryProps } from '../types';
import {
  AllCapsText,
  Container,
  FreeDeliveryIcon,
  Message,
  MessageContainer,
  MessageWrapper,
  ProgressBar,
  RemainingAmount,
} from './styles';

export const FreeDelivery: React.FC<FreeDeliveryProps> = (props) => {
  const {
    addAmountMessage,
    className,
    freeDeliveryMessage,
    discountedDeliveryFee = 0,
    discountedDeliveryThreshold = DEFAULT_FREE_DELIVERY_THRESHOLD,
    showFreeDelivery = true,
    isOnDarkBackground = false,
    total,
  } = props;
  const percent = Math.min(discountedDeliveryThreshold === 0 ? 0 : (total / discountedDeliveryThreshold) * 100, 100);
  const remainingAmount = Math.max(0, discountedDeliveryThreshold - total);
  const [isMounted, setIsMounted] = useState(false);
  const [isFreeDelivery, setIsFreeDelivery] = useState(percent === 100);

  const addAmountMessageRef = useRef<HTMLDivElement>(null);
  const freeDeliveryMessageRef = useRef<HTMLDivElement>(null);

  const getMessageContainerHeight = () => {
    if (isFreeDelivery) {
      return freeDeliveryMessageRef?.current ? freeDeliveryMessageRef.current.offsetHeight : 'auto';
    }
    return addAmountMessageRef?.current ? addAmountMessageRef.current.offsetHeight : 'auto';
  };

  const getMessageTransform = () => {
    if (addAmountMessageRef?.current) {
      return `translateY(${isFreeDelivery ? `-${addAmountMessageRef.current.offsetHeight}px` : '0px'})`;
    }
    return `translateY(${isFreeDelivery ? '-100%' : '0%'})`;
  };

  const [prevRemainingAmount, setPrevRemainingAmount] = useState(remainingAmount);
  useEffect(() => setPrevRemainingAmount(total), [total]);
  useOnMount(() => setIsMounted(true));

  const animatedPrice = useSpring({
    val: remainingAmount,
    from: { val: prevRemainingAmount },
    config: { duration: Transition.TimeValue.DOUBLE },
    immediate: !isMounted,
  });

  if (!showFreeDelivery) {
    return null;
  }

  const displayedAddAmountMessage = addAmountMessage ?? (
    <Message ref={addAmountMessageRef} style={{ transform: getMessageTransform() }} isShowing={!isFreeDelivery}>
      <FreeDeliveryIcon />
      <TextReplace
        replaceText={[
          <RemainingAmount key={'remaining-amount'}>
            {to(animatedPrice.val, (value) => {
              if (value === 0 && !isFreeDelivery) {
                setIsFreeDelivery(true);
              } else if (value !== 0 && isFreeDelivery) {
                setIsFreeDelivery(false);
              }
              return formatCurrency(value);
            })}
          </RemainingAmount>,
          <AllCapsText key={'free-delivery'}>
            {discountedDeliveryFee === 0 ? 'Free' : formatCurrency(discountedDeliveryFee)} {DISCOUNTED_DELIVERY_TEXT}
          </AllCapsText>,
        ]}
        text={ADD_AMOUNT_MESSAGE}
      />
    </Message>
  );

  const displayedFreeDeliveryMessage = freeDeliveryMessage ?? (
    <Message ref={freeDeliveryMessageRef} style={{ transform: getMessageTransform() }} isShowing={isFreeDelivery}>
      <FreeDeliveryIcon />
      <TextReplace
        replaceText={discountedDeliveryFee === 0 ? 'free' : formatCurrency(discountedDeliveryFee)}
        text={DISCOUNTED_DELIVERY_MESSAGE}
      />
    </Message>
  );

  return (
    <Container className={className}>
      <MessageContainer style={{ height: getMessageContainerHeight() }}>
        <MessageWrapper>
          {displayedAddAmountMessage}
          {displayedFreeDeliveryMessage}
        </MessageWrapper>
      </MessageContainer>
      <ProgressBar
        percent={percent}
        startingPercent={percent}
        isOnDarkBackground={isOnDarkBackground}
        renderBarGlowContent={(hasAnimatedOnce: boolean) => (
          <>{hasAnimatedOnce && <Confetti {...SHARED_CONFETTI_PROPS} active={isFreeDelivery} />}</>
        )}
      />
    </Container>
  );
};
